/**
 * Payment Details Modal Component
 *
 * Displays detailed payment information in a modal dialog
 */

import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { Button } from '@wordpress/components';
import PaymentStatusBadge from './PaymentStatusBadge';
import {
    formatCurrency,
    formatDate,
    getGatewayLabel,
    copyToClipboard,
} from '../utils/paymentHelpers';

/**
 * PaymentDetailsModal Component
 *
 * @param {Object} props - Component props
 * @param {Object|null} props.payment - Payment object to display
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} [props.onMarkPaid] - Callback after payment is marked as paid (refetch list)
 * @param {boolean} props.isOpen - Whether modal is open
 * @return {JSX.Element|null} Modal element or null if not open
 */
const PaymentDetailsModal = ({ payment, onClose, onMarkPaid, isOpen }) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const [isMarkingPaid, setIsMarkingPaid] = useState(false);
    const [markPaidError, setMarkPaidError] = useState(null);

    if (!isOpen || !payment) {
        return null;
    }

    const handleCopyTransactionId = async () => {
        const success = await copyToClipboard(payment.transaction_id);
        if (success) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleMarkAsPaid = async () => {
        if (!payment?.id || !onMarkPaid) return;
        setMarkPaidError(null);
        setIsMarkingPaid(true);
        try {
            const response = await apiFetch({
                path: `/nemtly-booking/v1/payments/${payment.id}/mark-paid`,
                method: 'POST',
            });
            if (response?.success) {
                onMarkPaid();
                onClose();
            } else {
                setMarkPaidError(response?.message || __('Failed to mark as paid.', 'nemtly-booking'));
            }
        } catch (err) {
            setMarkPaidError(err?.message || __('Failed to mark as paid.', 'nemtly-booking'));
        } finally {
            setIsMarkingPaid(false);
        }
    };

    return (
        <div
            className="nb-fixed nb-inset-0 nb-z-50 nb-overflow-y-auto"
            onClick={handleBackdropClick}
        >
            <div className="nb-flex nb-min-h-screen nb-items-center nb-justify-center nb-p-4">
                {/* Backdrop */}
                <div className="nb-fixed nb-inset-0 nb-bg-black nb-bg-opacity-50 nb-transition-opacity" />

                {/* Modal */}
                <div className="nb-relative nb-w-full nb-max-w-2xl nb-rounded-lg nb-bg-white nb-shadow-xl">
                    {/* Header */}
                    <div className="nb-flex nb-items-center nb-justify-between nb-border-b nb-border-gray-200 nb-px-6 nb-py-4">
                        <h2 className="nb-text-xl nb-font-semibold nb-text-gray-900">
                            {__('Payment Details', 'nemtly-booking')}
                        </h2>
                        <button
                            onClick={onClose}
                            className="nb-text-gray-400 hover:nb-text-gray-600 nb-transition-colors"
                        >
                            <svg
                                className="nb-h-6 nb-w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="nb-px-6 nb-py-4">
                        <div className="nb-space-y-6">
                            {/* Payment Info Section */}
                            <div>
                                <h3 className="nb-text-sm nb-font-medium nb-text-gray-500 nb-mb-3">
                                    {__('Payment Information', 'nemtly-booking')}
                                </h3>
                                <dl className="nb-grid nb-grid-cols-2 nb-gap-4">
                                    <div>
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Payment ID', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                            #{payment.id}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Status', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1">
                                            <PaymentStatusBadge status={payment.status} />
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Amount', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1 nb-text-sm nb-font-semibold nb-text-gray-900">
                                            {formatCurrency(payment.amount, payment.currency)}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Gateway', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                            {getGatewayLabel(payment.gateway)}
                                        </dd>
                                    </div>
                                    <div className="nb-col-span-2">
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Transaction ID', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1 nb-flex nb-items-center nb-gap-2">
                                            <code className="nb-text-sm nb-text-gray-900 nb-bg-gray-100 nb-px-2 nb-py-1 nb-rounded">
                                                {payment.transaction_id}
                                            </code>
                                            <button
                                                onClick={handleCopyTransactionId}
                                                className="nb-text-blue-600 hover:nb-text-blue-800 nb-text-sm"
                                            >
                                                {copySuccess
                                                    ? __('Copied!', 'nemtly-booking')
                                                    : __('Copy', 'nemtly-booking')}
                                            </button>
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                            {__('Created', 'nemtly-booking')}
                                        </dt>
                                        <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                            {formatDate(payment.created_at)}
                                        </dd>
                                    </div>
                                    {payment.updated_at && payment.updated_at !== payment.created_at && (
                                        <div>
                                            <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                {__('Updated', 'nemtly-booking')}
                                            </dt>
                                            <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                {formatDate(payment.updated_at)}
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            </div>

                            {/* Booking Info Section */}
                            {payment.booking && (
                                <div className="nb-border-t nb-border-gray-200 nb-pt-6">
                                    <h3 className="nb-text-sm nb-font-medium nb-text-gray-500 nb-mb-3">
                                        {__('Booking Information', 'nemtly-booking')}
                                    </h3>
                                    <dl className="nb-grid nb-grid-cols-2 nb-gap-4">
                                        <div>
                                            <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                {__('Booking ID', 'nemtly-booking')}
                                            </dt>
                                            <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                {payment.booking.uuid}
                                            </dd>
                                        </div>
                                        {payment.booking.event && (
                                            <div className="nb-col-span-2">
                                                <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                    {__('Event', 'nemtly-booking')}
                                                </dt>
                                                <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                    {payment.booking.event.title}
                                                </dd>
                                            </div>
                                        )}
                                        <div>
                                            <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                {__('Customer Name', 'nemtly-booking')}
                                            </dt>
                                            <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                {payment.booking.attendee_name}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                {__('Customer Email', 'nemtly-booking')}
                                            </dt>
                                            <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                {payment.booking.attendee_email}
                                            </dd>
                                        </div>
                                        {payment.booking.attendee_phone && (
                                            <div>
                                                <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                    {__('Customer Phone', 'nemtly-booking')}
                                                </dt>
                                                <dd className="nb-mt-1 nb-text-sm nb-text-gray-900">
                                                    {payment.booking.attendee_phone}
                                                </dd>
                                            </div>
                                        )}
                                        <div>
                                            <dt className="nb-text-sm nb-font-medium nb-text-gray-500">
                                                {__('Booking Status', 'nemtly-booking')}
                                            </dt>
                                            <dd className="nb-mt-1 nb-text-sm nb-text-gray-900 nb-capitalize">
                                                {payment.booking.booking_status}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            )}

                            {/* Gateway Response Section */}
                            {payment.gateway_response && (
                                <div className="nb-border-t nb-border-gray-200 nb-pt-6">
                                    <h3 className="nb-text-sm nb-font-medium nb-text-gray-500 nb-mb-3">
                                        {__('Gateway Response', 'nemtly-booking')}
                                    </h3>
                                    <pre className="nb-text-xs nb-bg-gray-50 nb-p-3 nb-rounded nb-overflow-auto nb-max-h-40">
                                        {JSON.stringify(JSON.parse(payment.gateway_response), null, 2)}
                                    </pre>
                                </div>
                            )}
                            {payment.status === 'pending' && onMarkPaid && (
                                <div className="nb-border-t nb-border-gray-200 nb-pt-6">
                                    {markPaidError && (
                                        <p className="nb-text-sm nb-text-red-600 nb-mb-3">{markPaidError}</p>
                                    )}
                                    <p className="nb-text-sm nb-text-gray-600 nb-mb-3">
                                        {__('Mark this payment as received to confirm the booking.', 'nemtly-booking')}
                                    </p>
                                    <Button
                                        isBusy={isMarkingPaid}
                                        disabled={isMarkingPaid}
                                        onClick={handleMarkAsPaid}
                                    >
                                        {isMarkingPaid ? __('Marking…', 'nemtly-booking') : __('Mark as paid', 'nemtly-booking')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="nb-flex nb-justify-end nb-gap-3 nb-border-t nb-border-gray-200 nb-px-6 nb-py-4">
                        {payment.booking && (
                            <a
                                href={`admin.php?page=nemtly-booking-bookings#/bookings/${payment.booking.id}`}
                                className="nb-inline-flex nb-items-center nb-px-4 nb-py-2 nb-border nb-border-gray-300 nb-rounded-md nb-shadow-sm nb-text-sm nb-font-medium nb-text-gray-700 nb-bg-white hover:nb-bg-gray-50 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-offset-2 focus:nb-ring-blue-500"
                            >
                                {__('View Booking', 'nemtly-booking')}
                            </a>
                        )}
                        <button
                            onClick={onClose}
                            className="nb-inline-flex nb-items-center nb-px-4 nb-py-2 nb-border nb-border-transparent nb-rounded-md nb-shadow-sm nb-text-sm nb-font-medium nb-text-white nb-bg-blue-600 hover:nb-bg-blue-700 focus:nb-outline-none focus:nb-ring-2 focus:nb-ring-offset-2 focus:nb-ring-blue-500"
                        >
                            {__('Close', 'nemtly-booking')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentDetailsModal;
